import Suspense from '@/Components/Suspense';
import { AlertDialog } from '@/Components/ui/AlertDialog';
import {
  type Integration_ZohoUpdateSettings_Mutation,
  zohoDeleteAuth,
  zohoUpdateSettings,
} from '@/graphql/mutations/Integration.mutation';
import {
  type Integration_ZohoInfo_Query,
  type Integration_ZohoInfo_Query$data,
  type Integration_ZohoInfo_Query$variables,
  ZohoInfoQuery,
} from '@/graphql/queries/Integration.query';
import { ArrowBack, Cached, Settings } from '@mui/icons-material';
import { Button, Grid2, IconButton, Skeleton, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  type PreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import { toast } from 'react-toastify';

type GqlProps = {
  zohoInfoRef: PreloadedQuery<Integration_ZohoInfo_Query, Record<string, unknown>>;
  loadZohoInfo: (variables: Integration_ZohoInfo_Query$variables, options?: UseQueryLoaderLoadQueryOptions) => void;
};

const Zoho = (props: GqlProps) => {
  const { zohoInfo } = usePreloadedQuery<Integration_ZohoInfo_Query>(ZohoInfoQuery, props.zohoInfoRef);
  const [showSettings, setShowSettings] = useState(false);

  const onZohoClick = () => {
    const params = {
      response_type: 'code',
      client_id: '1000.OQVVS67MN4YNI1EZLZIRZZPYKS7LAF',
      scope:
        'AaaServer.profile.READ,ZohoCRM.modules.ALL,ZohoCRM.settings.ALL,ZohoCRM.users.ALL,SalesIQ.visitordetails.READ,SalesIQ.apps.READ,SalesIQ.visitorhistoryviews.READ,SalesIQ.portals.READ,ZohoCRM.bulk.read,ZohoCRM.modules.leads.ALL,ZohoCRM.modules.contacts.ALL',
      redirect_uri: 'https://internal.zngly.com',
      access_type: 'offline',
      prompt: 'consent',
      state: JSON.stringify({ type: 'zoho', redirectUrl: `${window.location.origin}/auth/oauth-callback` }),
    };

    window.open(`https://accounts.zoho.com/oauth/v2/auth?${new URLSearchParams(params)}`, '_blank');
  };

  return (
    <Grid2 size={{ sm: 12, md: 6 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <h3 style={{ marginBottom: 'unset' }}>Zoho Integration </h3>
        {zohoInfo?.hasAuth && (
          <IconButton
            onClick={() => {
              setShowSettings((prev) => !prev);
            }}
          >
            <Settings />
          </IconButton>
        )}
      </Stack>

      <p>Connect Zngly to Zoho for deeper analytics insights</p>

      {!zohoInfo?.hasAuth && (
        <Button className="z-btn-2" onClick={onZohoClick}>
          Connect to Zoho
        </Button>
      )}
      {zohoInfo?.hasAuth && !showSettings && (
        <Stack direction="row" justifyContent="space-between">
          <Button className="z-btn-1">Connected</Button>

          <AlertDialog
            title="Reset Zoho Connection"
            description="Are you sure you want to reset your Zoho connection? "
            buttonElement={
              <Button className="z-btn-2" endIcon={<Cached />}>
                reset
              </Button>
            }
            acceptButton={({ closeDialog }) => (
              <Button
                className="z-btn-2 red"
                onClick={(e) => {
                  e.preventDefault();
                  zohoDeleteAuth(
                    { input: { remove: true } },
                    {
                      onCompleted: (data, errors) => {
                        if (errors || !data?.zohoDeleteAuth?.success) {
                          toast.error('Failed to delete Zoho connection. Please contact support.');
                          return;
                        }

                        props.loadZohoInfo({}, { fetchPolicy: 'network-only' });
                        toast.success('Successfully deleted Zoho connection');
                        closeDialog();
                      },
                    }
                  );
                }}
              >
                Reset
              </Button>
            )}
          />
        </Stack>
      )}

      {showSettings && zohoInfo?.hasAuth && <ZohoSettings zohoInfo={zohoInfo} loadZohoInfo={props.loadZohoInfo} />}
    </Grid2>
  );
};

const setSettingsHandler = (zohoInfo: ZohoSettingsProps['zohoInfo']) => {
  if (!zohoInfo?.salesIqPortalId) {
    return 'portal';
  }

  if (!zohoInfo?.salesIqAppId) {
    return 'app';
  }

  return 'done';
};

type ZohoSettingsProps = {
  zohoInfo: Integration_ZohoInfo_Query$data['zohoInfo'];
  loadZohoInfo: GqlProps['loadZohoInfo'];
};
const ZohoSettings = ({ zohoInfo, loadZohoInfo }: ZohoSettingsProps) => {
  const [step, setStep] = useState<'portal' | 'app' | 'done'>(setSettingsHandler(zohoInfo));

  useEffect(() => {
    setStep(setSettingsHandler(zohoInfo));
  }, [zohoInfo]);

  const onClickHandler = (input: Integration_ZohoUpdateSettings_Mutation['variables']['input']) => {
    if (step === 'portal' && zohoInfo?.salesIqPortalId === input?.salesIqPortalId && input?.salesIqPortalId !== null) {
      setStep('app');
      return;
    }

    zohoUpdateSettings(
      { input },
      {
        onCompleted: (data, errors) => {
          if (!!errors?.length) {
            toast.error('Failed to update Zoho settings. Please contact support.');
            return;
          }

          if (step === 'portal') {
            toast.success('Successfully set Zoho SalesIq Portal');
          }
          if (step === 'app') {
            toast.success('Successfully set Zoho SalesIq App');
          }
          loadZohoInfo({ salesIqPortalId: input?.salesIqPortalId }, { fetchPolicy: 'network-only' });
        },
      }
    );
  };

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: '5px',
        padding: '0.5em',
        margin: '1em 0em',
      }}
    >
      <h3>SalesIq Settings: {step === 'portal' ? 'Select Portal' : step === 'app' ? 'Select App' : 'Done'}</h3>

      <Stack
        direction="column"
        spacing={1}
        sx={{
          marginTop: '1em',
          border: '1px solid #e0e0e0',
          borderRadius: '5px',
          padding: '0.5em',
          margin: '1em 0em',
        }}
      >
        {step === 'done' && (
          <Button className="z-btn-6" fullWidth onClick={() => setStep('portal')} endIcon={<Cached />}>
            Reconfigure SalesIq
          </Button>
        )}
        {step === 'portal' &&
          zohoInfo?.salesIqPortals?.map((portal) => (
            <Button
              key={portal?.id}
              className="z-btn-6"
              fullWidth
              sx={{
                ...(portal?.screenName === zohoInfo?.salesIqPortalId && {
                  backgroundColor: 'var(--z-selected-clr-0) !important',
                }),
              }}
              onClick={() =>
                onClickHandler({
                  salesIqPortalId: portal?.screenName,
                })
              }
            >
              Account Portal: {portal?.name}
            </Button>
          ))}

        {step === 'app' &&
          zohoInfo?.salesIqApps?.map((app) => {
            return (
              <Button
                key={app?.id}
                className="z-btn-6"
                fullWidth
                sx={{
                  ...(app?.id === zohoInfo?.salesIqAppId && {
                    backgroundColor: 'var(--z-selected-clr-0) !important',
                  }),
                }}
                disabled={app?.id === zohoInfo?.salesIqAppId}
                onClick={() =>
                  onClickHandler({
                    salesIqPortalId: zohoInfo?.salesIqPortalId,
                    salesIqAppId: app?.id,
                    salesIqAppCode: app?.code,
                  })
                }
              >
                App: {app?.name}
              </Button>
            );
          })}
      </Stack>

      {step === 'app' && (
        <Button className="z-btn-2" startIcon={<ArrowBack />} fullWidth onClick={() => setStep('portal')}>
          Back To Portals
        </Button>
      )}
    </div>
  );
};

const Fallback = () => {
  return (
    <Grid2 size={{ sm: 12, md: 6 }}>
      <h3>Zoho Integration</h3>
      <p>Connect Zngly to Zoho for deeper analytics insights</p>
      <Skeleton variant="text" className="zn-sk-1" />
    </Grid2>
  );
};

const ZohoWrapper = () => {
  const [zohoInfoRef, loadZohoInfo, disposeZohoInfo] = useQueryLoader<Integration_ZohoInfo_Query>(ZohoInfoQuery);

  useEffect(() => {
    loadZohoInfo({}, { fetchPolicy: 'store-or-network' });

    return () => {
      disposeZohoInfo();
    };
  }, []);

  return (
    <Suspense fallback={<Fallback />} exists={zohoInfoRef}>
      <Zoho zohoInfoRef={zohoInfoRef!} loadZohoInfo={loadZohoInfo} />
    </Suspense>
  );
};

export default ZohoWrapper;
